import { Box, Card, CardContent, Typography, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import { CommonHelper } from 'src/utils/commonHelper';

const SummaryCommonCard = ({ title, data, percentage, percentageState, averageData }) => {
  const { t: translate } = useTranslation();
  const isPositive = percentageState !== 'down';

  return (
    <Card 
      sx={{ 
        width: '100%',
        height: '100%',
        minHeight: 160,
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.06)',
        transition: 'all 0.2s ease-in-out',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
        }
      }}
    >
      <CardContent 
        sx={{ 
          p: 2.5, 
          '&:last-child': { pb: 2.5 },
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          justifyContent: 'space-between',
          flex: 1
        }}
      >
        {/* Title Section */}
        <Box sx={{ flex: '0 0 auto' }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              color: 'text.secondary',
              fontWeight: 500,
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 1.5,
              lineHeight: 1.2
            }}
          >
            {title}
          </Typography>

          {/* Main Data Section */}
          <Box display="flex" alignItems="center" sx={{ gap: 1.5, flexWrap: 'wrap' }}>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 700,
                color: 'primary.main',
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                lineHeight: 1.1,
                flex: '0 0 auto'
              }}
            >
              {CommonHelper.formatNumber(data) || 0}
            </Typography>
            
            {percentage && (
              <Chip
                icon={isPositive ? <TrendingUp /> : <TrendingDown />}
                label={`${percentage}%`}
                size="small"
                sx={{
                  backgroundColor: isPositive ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                  color: isPositive ? '#4CAF50' : '#F44336',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: 'auto',
                  '& .MuiChip-icon': {
                    color: isPositive ? '#4CAF50' : '#F44336',
                    fontSize: '1rem'
                  }
                }}
              />
            )}
          </Box>
        </Box>

        {/* Average Data Section */}
        {averageData && (
          <Box 
            sx={{ 
              pt: 1.5,
              borderTop: '1px solid rgba(0, 0, 0, 0.06)',
              flex: '0 0 auto',
              mt: 'auto'
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontSize: '0.8rem',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                lineHeight: 1.3
              }}
            >
              <Box 
                component="span" 
                sx={{ 
                  width: 4, 
                  height: 4, 
                  borderRadius: '50%', 
                  backgroundColor: 'primary.main',
                  opacity: 0.6,
                  flex: '0 0 auto'
                }} 
              />
              {translate("report.average_per_day")}: 
              <Box 
                component="span" 
                sx={{ 
                  fontWeight: 600, 
                  color: 'text.primary',
                  ml: 0.25
                }}
              >
                {averageData || '<1'}
              </Box>
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default SummaryCommonCard;
